import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useAuth } from '../../auth/AuthContext';
import {
  readOfflineMediaCache,
  writeOfflineMediaCache,
} from '../../utils/offlineMediaCache';
import { useOnlineStatus } from '../../utils/onlineStatus';
import {
  fetchApprovedMediaAccessUrl,
  fetchMediaBlob,
  fetchVideoObjectUrl,
} from '../../utils/mediaUploads';

function getMediaLabel(media = {}) {
  return media?.mediaType === 'video' ? 'Video' : 'Image';
}

function getUnavailableMessage(mediaType, online) {
  if (!online) return mediaType === 'video' ? 'Video available when online' : 'Image available when online';
  return mediaType === 'video' ? 'Video could not load' : 'Image could not load';
}

function formatMediaTime(value) {
  const totalSeconds = Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export default function MediaAttachment({ media, className = '', compact = false }) {
  const videoRef = useRef(null);
  const videoRequestRef = useRef(null);
  const playbackObjectUrlsRef = useRef([]);
  const { user } = useAuth();
  const online = useOnlineStatus();
  const [url, setUrl] = useState('');
  const [posterUrl, setPosterUrl] = useState('');
  const [error, setError] = useState('');
  const [videoLoadError, setVideoLoadError] = useState('');
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoUnavailable, setVideoUnavailable] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [playWhenReady, setPlayWhenReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [lightboxAspectRatio, setLightboxAspectRatio] = useState('16 / 9');
  const [lightboxAspectRatioValue, setLightboxAspectRatioValue] = useState('1.777778');
  const mediaType = media?.mediaType || 'image';
  const storagePath = media?.storagePath || '';
  const thumbnailPath = media?.thumbnailPath || '';
  const uploadId = media?.uploadId || '';
  const currentUserId = user?.uid || '';

  useEffect(() => () => {
    playbackObjectUrlsRef.current.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
    playbackObjectUrlsRef.current = [];
  }, []);

  useEffect(() => {
    let cancelled = false;
    const objectUrls = [];
    let timeoutId = 0;
    if (!storagePath) {
      setUrl('');
      setPosterUrl('');
      setError('');
      setVideoLoadError('');
      setVideoLoading(false);
      setVideoUnavailable(false);
      return undefined;
    }

    setError('');
    setUrl('');
    setPosterUrl('');
    setVideoLoadError('');
    setVideoLoading(false);
    setVideoUnavailable(false);
    timeoutId = window.setTimeout(() => {
      if (!cancelled) setError(getUnavailableMessage(mediaType, online));
    }, 20000);

    const makeObjectUrl = (blob) => {
      const objectUrl = URL.createObjectURL(blob);
      objectUrls.push(objectUrl);
      return objectUrl;
    };
    let hasPoster = false;
    let hasImage = false;

    const loadImage = async () => {
      const cached = currentUserId
        ? await readOfflineMediaCache({ userId: currentUserId, storagePath })
        : null;
      if (cached?.blob && !cancelled) {
        window.clearTimeout(timeoutId);
        hasImage = true;
        setUrl(makeObjectUrl(cached.blob));
      }

      if (!online && cached?.blob) return;
      if (!online) throw new Error(getUnavailableMessage('image', online));

      const blob = await fetchMediaBlob(storagePath, undefined, { uploadId });
      if (currentUserId) {
        writeOfflineMediaCache({
          userId: currentUserId,
          storagePath,
          blob,
          contentType: blob.type,
          mediaType: 'image',
        }).catch(() => {});
      }
      if (!cancelled) {
        window.clearTimeout(timeoutId);
        hasImage = true;
        setUrl(makeObjectUrl(blob));
      }
    };

    const loadPoster = async () => {
      if (!thumbnailPath) return '';
      const cached = currentUserId
        ? await readOfflineMediaCache({ userId: currentUserId, storagePath: thumbnailPath })
        : null;
      if (cached?.blob && !cancelled) {
        const nextPosterUrl = makeObjectUrl(cached.blob);
        hasPoster = true;
        setPosterUrl(nextPosterUrl);
        return nextPosterUrl;
      }
      const blob = await fetchMediaBlob(thumbnailPath, undefined, { uploadId }).catch(() => null);
      if (!blob) return '';
      if (currentUserId) {
        writeOfflineMediaCache({
          userId: currentUserId,
          storagePath: thumbnailPath,
          blob,
          contentType: blob.type,
          mediaType: 'image',
        }).catch(() => {});
      }
      if (cancelled) return '';
      const nextPosterUrl = makeObjectUrl(blob);
      hasPoster = true;
      setPosterUrl(nextPosterUrl);
      return nextPosterUrl;
    };

    const loadVideoPoster = async () => {
      const nextPosterUrl = await loadPoster();
      if (!cancelled && nextPosterUrl) window.clearTimeout(timeoutId);
    };

    (mediaType === 'video' ? loadVideoPoster() : loadImage())
      .catch(() => {
        if (!cancelled) {
          window.clearTimeout(timeoutId);
          if (mediaType === 'image' && hasImage) {
            return;
          }
          if (mediaType === 'video' && hasPoster) {
            return;
          } else {
            setError(getUnavailableMessage(mediaType, online));
          }
        }
      });

    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
      objectUrls.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
    };
  }, [currentUserId, mediaType, online, storagePath, thumbnailPath, uploadId]);

  useEffect(() => {
    setIsPlaying(false);
    setDuration(0);
    setCurrentTime(0);
    setLightboxAspectRatio('16 / 9');
    setLightboxAspectRatioValue('1.777778');
  }, [url]);

  useEffect(() => {
    if (!url || !playWhenReady) return;
    const video = videoRef.current;
    if (!video) return;
    setPlayWhenReady(false);
    const playPromise = video.play();
    if (playPromise?.catch) playPromise.catch(() => {});
  }, [playWhenReady, url]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const label = getMediaLabel(media);
  const rootClass = ['message-media', className].filter(Boolean).join(' ');
  if (media?.referenceStatus === 'deleted' || media?.deleted || !media?.storagePath) {
    return <div className={`${rootClass} message-media--error`}>{label} no longer available</div>;
  }

  const altText = media.fileName || label;

  const lightbox = isOpen ? (
    <div
      className="message-media-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`${label} preview`}
      onClick={() => setIsOpen(false)}
    >
      <button
        type="button"
        className="message-media-lightbox__close"
        onClick={() => setIsOpen(false)}
        aria-label="Close media preview"
      >
        x
      </button>
      <div
        className={media.mediaType === 'video' ? 'message-media-lightbox__stage message-media-lightbox__stage--video' : 'message-media-lightbox__stage message-media-lightbox__stage--image'}
        onClick={(event) => {
          if (media.mediaType === 'video') event.stopPropagation();
        }}
      >
        {media.mediaType === 'video' ? (
          <div
            className="message-media-lightbox__video-frame"
            style={{
              '--media-aspect-ratio': lightboxAspectRatio,
              '--media-aspect-ratio-value': lightboxAspectRatioValue,
            }}
          >
            <video
              className="message-media-lightbox__media message-media-lightbox__media--video"
              src={url}
              poster={posterUrl || undefined}
              controls
              autoPlay
              playsInline
              onLoadedMetadata={(event) => {
                const { videoWidth, videoHeight } = event.currentTarget;
                if (videoWidth > 0 && videoHeight > 0) {
                  setLightboxAspectRatio(`${videoWidth} / ${videoHeight}`);
                  setLightboxAspectRatioValue(String(videoWidth / videoHeight));
                }
              }}
            />
          </div>
        ) : (
          <button
            type="button"
            className="message-media-lightbox__image"
            style={{ backgroundImage: `url("${url}")` }}
            aria-label={`Close ${altText} preview`}
            onClick={() => setIsOpen(false)}
          />
        )}
      </div>
    </div>
  ) : null;

  if (error && !(media.mediaType === 'video' && posterUrl)) {
    return <div className={`${rootClass} message-media--error`}>{error}</div>;
  }
  if (!url && !(media.mediaType === 'video' && posterUrl)) {
    return <div className={`${rootClass} message-media--loading`}>Loading {label.toLowerCase()}...</div>;
  }
  if (media.mediaType === 'video') {
    const canPlayVideo = Boolean(url && !videoUnavailable);
    const canRequestVideo = Boolean(storagePath && !videoLoading);
    const shouldShowVideoPoster = Boolean(posterUrl && (!canPlayVideo || (!isPlaying && currentTime <= 0.05)));
    const unavailableMessage = getUnavailableMessage('video', online);
    const ensureVideoUrl = async () => {
      if (canPlayVideo) return url;
      if (videoRequestRef.current) return videoRequestRef.current;

      setVideoLoading(true);
      setVideoLoadError('');
      setVideoUnavailable(false);
      const request = (uploadId
        ? fetchApprovedMediaAccessUrl({ uploadId, storagePath })
        : fetchVideoObjectUrl(storagePath, { uploadId }))
        .then((nextUrl) => {
          if (!nextUrl) throw new Error('Video URL is missing.');
          if (nextUrl.startsWith('blob:')) playbackObjectUrlsRef.current.push(nextUrl);
          setUrl(nextUrl);
          setVideoUnavailable(false);
          return nextUrl;
        })
        .catch((err) => {
          setVideoUnavailable(true);
          setVideoLoadError(unavailableMessage);
          throw err;
        })
        .finally(() => {
          videoRequestRef.current = null;
          setVideoLoading(false);
        });
      videoRequestRef.current = request;
      return request;
    };
    const openVideoPreview = async () => {
      try {
        await ensureVideoUrl();
        setIsOpen(true);
      } catch (err) {
        // Keep the thumbnail visible and show the retryable status on the card.
      }
    };
    const toggleVideoPlayback = async () => {
      if (!canPlayVideo) {
        setPlayWhenReady(true);
        try {
          await ensureVideoUrl();
        } catch (err) {
          setPlayWhenReady(false);
        }
        return;
      }
      const video = videoRef.current;
      if (!video) return;
      if (video.paused) {
        video.play().catch(() => null);
      } else {
        video.pause();
      }
    };

    const handleSeek = (event) => {
      const nextTime = Number(event.target.value || 0);
      const video = videoRef.current;
      if (video) video.currentTime = nextTime;
      setCurrentTime(nextTime);
    };

    if (compact) {
      return (
        <>
          <button
            type="button"
            className={`${rootClass} message-media--compact-video`}
            onClick={openVideoPreview}
            aria-label={videoLoading ? 'Loading video' : 'Open video full screen'}
            disabled={!canRequestVideo && !canPlayVideo}
          >
            <span
              className="message-media__compact-video-thumb"
              style={posterUrl ? { backgroundImage: `url("${posterUrl}")` } : undefined}
            >
              <span className="message-media__compact-video-icon" aria-hidden="true" />
              {videoLoading && <span className="message-media__compact-video-status">Loading video...</span>}
              {!videoLoading && videoLoadError && <span className="message-media__compact-video-status">{videoLoadError}</span>}
            </span>
          </button>
          {lightbox && createPortal(lightbox, document.body)}
        </>
      );
    }

    return (
      <>
        <div className={`${rootClass} message-media--video-wrap`}>
          <button
            type="button"
            className={`message-media__video-surface ${canPlayVideo ? '' : 'message-media__video-surface--offline'}`}
            onClick={toggleVideoPlayback}
            aria-label={canPlayVideo ? (isPlaying ? 'Pause video' : 'Play video') : (videoLoading ? 'Loading video' : 'Play video')}
            disabled={!canRequestVideo && !canPlayVideo}
          >
            {canPlayVideo ? (
              <video
                ref={videoRef}
                className="message-media__asset message-media--video"
                src={url}
                poster={posterUrl || undefined}
                playsInline
                preload="metadata"
                onLoadedMetadata={(event) => setDuration(Number(event.currentTarget.duration || 0))}
                onTimeUpdate={(event) => setCurrentTime(Number(event.currentTarget.currentTime || 0))}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
              />
            ) : !posterUrl ? (
              <span
                className="message-media__video-poster"
              >
                <span className="message-media__video-status">{videoLoading ? 'Loading video...' : (videoLoadError || unavailableMessage)}</span>
              </span>
            ) : null}
            {shouldShowVideoPoster && (
              <span
                className={`message-media__video-poster ${canPlayVideo ? 'message-media__video-poster--overlay' : ''}`}
                style={{ backgroundImage: `url("${posterUrl}")` }}
                aria-hidden={canPlayVideo ? 'true' : undefined}
              >
                {canPlayVideo ? (
                  <span className="message-media__video-play-indicator" aria-hidden="true" />
                ) : videoLoading ? (
                  <span className="message-media__video-status">Loading video...</span>
                ) : videoLoadError ? (
                  <span className="message-media__video-status">{videoLoadError}</span>
                ) : (
                  <span className="message-media__video-play-indicator" aria-hidden="true" />
                )}
              </span>
            )}
          </button>
          <div className="message-media-controls">
            <button type="button" onClick={toggleVideoPlayback} disabled={!canRequestVideo && !canPlayVideo}>
              {videoLoading ? 'Loading' : (isPlaying ? 'Pause' : 'Play')}
            </button>
            <input
              type="range"
              min="0"
              max={duration || 0}
              step="0.05"
              value={Math.min(currentTime, duration || 0)}
              onChange={handleSeek}
              aria-label="Video playback position"
              disabled={!canPlayVideo}
            />
            <span>{formatMediaTime(currentTime)} / {formatMediaTime(duration)}</span>
            <button type="button" onClick={openVideoPreview} aria-label="Open video full screen" disabled={!canRequestVideo && !canPlayVideo}>
              Full screen
            </button>
          </div>
        </div>
        {lightbox && createPortal(lightbox, document.body)}
      </>
    );
  }
  return (
    <>
      <button
        type="button"
        className={`${rootClass} message-media__trigger`}
        onClick={() => setIsOpen(true)}
        aria-label="Open image full screen"
      >
        <img className="message-media__asset message-media--image" src={url} alt={altText} loading="lazy" />
      </button>
      {lightbox && createPortal(lightbox, document.body)}
    </>
  );
}

MediaAttachment.propTypes = {
  media: PropTypes.shape({
    provider: PropTypes.string,
    uploadId: PropTypes.string,
    mediaType: PropTypes.string,
    storagePath: PropTypes.string,
    thumbnailPath: PropTypes.string,
    fileName: PropTypes.string,
    referenceStatus: PropTypes.string,
    deleted: PropTypes.bool,
  }),
  className: PropTypes.string,
  compact: PropTypes.bool,
};
